/**
 * The concept panel over the lesson mind map: what a concept node means.
 *
 * Opened by clicking (or pressing Enter on) a concept node. Its content is
 * the lesson plan's, so it is there from the moment the map is: the
 * concept's visual (once made), the description, a "Know first" box of prerequisites in the top-right corner,
 * the learning goal, key points and common mistakes. While the visual is still
 * being made, a placeholder holds its place so the text below never jumps. A
 * concept with supporting visuals (one per key point that needs a picture)
 * shows its anchor first and the others in a small strip below it; picking one
 * shows it in the anchor's place. The student's progress
 * on it shows once the concept has reached Mindspace.
 *
 * Every text field is model-written and may carry Markdown or maths, so it is
 * rendered through InlineMarkdown (eyebrow, title) or BlockMarkdown
 * (description).
 *
 * A prerequisite leads to another concept of this lesson (the panel moves to
 * it and the map to its node), to a concept from another lesson (the panel
 * shows that one, with Back), or nowhere (plain text).
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ArrowLeft, Loader2, Shapes, X } from 'lucide-react';
import { cn } from '@ai-tutor/ui';
import {
  BlockMarkdown,
  InlineMarkdown,
} from '@/components/core/markdown/markdown';
import {
  VisualBlock,
  VisualPlaceholder,
  VisualThumb,
  hasThumb,
} from '@/components/core/visual';
import { ConceptNotes } from '@/components/features/mindspace/concepts/ConceptNotes';
import { MasteryBadge } from '@/components/features/mindspace/concepts/ConceptBadges';
import {
  PrerequisitesBox,
  type PrerequisiteItem,
} from '@/components/features/mindspace/concepts/PrerequisitesBox';
import { useConceptDetail } from '@/hooks/mindspace/useMindspace';
import type { MindmapConcept } from '@/types/api';
import type { Visual } from '@/types/visual';
import { stripMath } from '@/lib/markdown/previewText';
import { deriveMastery } from '@/utils/mindspaceDisplay';

export type ConceptPanelView =
  | { kind: 'lesson'; conceptId: string }
  | { kind: 'mindspace'; conceptId: string };

interface MindmapConceptPanelProps {
  view: ConceptPanelView;
  concepts: MindmapConcept[];
  onClose: () => void;
  /** Open a concept of this lesson (and move the map to its node). */
  onOpenLesson: (conceptId: string) => void;
  /** Open one of the student's Mindspace concepts from another lesson. */
  onOpenMindspace: (conceptId: string) => void;
  /** Present when there is a concept to go back to. */
  onBack?: () => void;
}

export function MindmapConceptPanel(props: MindmapConceptPanelProps) {
  const { view, onClose } = props;
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Keyboard users land on the concept they opened, and Escape closes it.
  useEffect(() => {
    headingRef.current?.focus();
  }, [view.kind, view.conceptId]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <aside
      aria-label='Concept'
      data-testid='mindmap-concept-panel'
      className='absolute bottom-3 right-3 top-3 z-10 flex w-[min(26rem,calc(100%-1.5rem))] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-lg duration-200 animate-in fade-in slide-in-from-right-4'
    >
      {view.kind === 'lesson' ? (
        <LessonConcept
          {...props}
          conceptId={view.conceptId}
          headingRef={headingRef}
        />
      ) : (
        <MindspaceConcept
          {...props}
          conceptId={view.conceptId}
          headingRef={headingRef}
        />
      )}
    </aside>
  );
}

/** The concept as the mind map endpoint sends it: its supporting visuals
 *  arrive alongside the anchor (not yet in the generated API types). */
type ConceptWithVisuals = MindmapConcept & {
  supporting_visuals?: Visual[];
};

type BodyProps = MindmapConceptPanelProps & {
  conceptId: string;
  headingRef: React.RefObject<HTMLHeadingElement>;
};

function LessonConcept({
  conceptId,
  concepts,
  onClose,
  onBack,
  onOpenLesson,
  onOpenMindspace,
  headingRef,
}: BodyProps) {
  const concept = concepts.find(c => c.concept_id === conceptId);
  if (!concept) return null;
  const prerequisites: PrerequisiteItem[] = concept.prerequisites.map(p => ({
    name: p.name,
    onOpen: p.concept_id
      ? () => onOpenLesson(p.concept_id as string)
      : p.mindspace_id
        ? () => onOpenMindspace(p.mindspace_id as string)
        : undefined,
  }));
  return (
    <Frame
      eyebrow={concept.section || 'In this lesson'}
      title={concept.name}
      onClose={onClose}
      onBack={onBack}
      headingRef={headingRef}
      footer={
        concept.mindspace_id ? (
          <Progress mindspaceId={concept.mindspace_id} />
        ) : (
          <p className='text-xs text-muted-foreground'>
            Being added to your Mindspace
          </p>
        )
      }
    >
      {concept.visual ? (
        <ConceptVisuals
          key={concept.concept_id}
          anchor={concept.visual}
          supporting={(concept as ConceptWithVisuals).supporting_visuals ?? []}
        />
      ) : (
        concept.visual_pending && <VisualPlaceholder />
      )}
      <Lead description={concept.description} prerequisites={prerequisites} />
      <ConceptNotes
        learningGoal={concept.learning_goal}
        keyPoints={concept.key_points}
        misconceptions={concept.misconceptions}
      />
    </Frame>
  );
}

function MindspaceConcept({
  conceptId,
  onClose,
  onBack,
  onOpenMindspace,
  headingRef,
}: BodyProps) {
  const { data, isLoading } = useConceptDetail(conceptId);
  if (isLoading || !data) {
    return (
      <div className='flex flex-1 items-center justify-center'>
        <Loader2
          className='h-5 w-5 animate-spin text-muted-foreground'
          aria-label='Loading concept'
        />
      </div>
    );
  }
  const prerequisites: PrerequisiteItem[] = (data.prerequisites ?? []).map(
    p => ({
      name: p.title || p.name,
      onOpen: p.concept_id
        ? () => onOpenMindspace(p.concept_id as string)
        : undefined,
    })
  );
  return (
    <Frame
      eyebrow='From your Mindspace'
      title={data.title}
      onClose={onClose}
      onBack={onBack}
      headingRef={headingRef}
      footer={<MasteryBadge level={deriveMastery(data)} />}
    >
      {data.visual && <VisualBlock visual={data.visual} compact />}
      <Lead description={data.description} prerequisites={prerequisites} />
      <ConceptNotes
        learningGoal={data.learning_goal}
        keyPoints={data.key_points}
        misconceptions={data.misconceptions}
      />
    </Frame>
  );
}

/** The anchor, and below it a strip of every visual of the concept (the
 *  anchor first) when it has supporting ones; the one picked is shown large. */
function ConceptVisuals({
  anchor,
  supporting,
}: {
  anchor: Visual;
  supporting: Visual[];
}) {
  const all = [anchor, ...supporting];
  const [shown, setShown] = useState(0);
  const current = all[shown] ?? anchor;
  return (
    <div className='space-y-2'>
      <VisualBlock visual={current} compact />
      {supporting.length > 0 && (
        <ul
          aria-label='Visuals of this concept'
          className='flex gap-2 overflow-x-auto pb-1 [scrollbar-width:thin]'
        >
          {all.map((visual, i) => {
            const name = visualName(visual, i);
            return (
              <li key={i} className='flex-shrink-0'>
                <button
                  type='button'
                  aria-pressed={shown === i}
                  aria-label={name}
                  title={name}
                  onClick={() => setShown(i)}
                  className={cn(
                    'tap-target flex h-14 w-14 items-center justify-center overflow-hidden rounded-lg border bg-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    shown === i
                      ? 'border-primary ring-1 ring-primary'
                      : 'border-border hover:border-foreground/40'
                  )}
                >
                  {hasThumb(visual) ? (
                    <VisualThumb
                      visual={visual}
                      className='h-full w-full rounded-none border-0'
                    />
                  ) : (
                    <Shapes
                      className='h-5 w-5 text-muted-foreground'
                      aria-hidden
                    />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function visualName(visual: Visual, index: number): string {
  if (index === 0) return 'Main visual';
  const alt = visual.kind === 'diagram' ? '' : visual.alt;
  // A tooltip and a screen-reader name are plain text: maths as words.
  return stripMath(visual.caption || alt || `Visual ${index + 1}`);
}

/** The description, with the "Know first" box in the top-right corner
 *  (stacked under it on a narrow panel). */
function Lead({
  description,
  prerequisites,
}: {
  description: string;
  prerequisites: PrerequisiteItem[];
}) {
  return (
    <div className='flex flex-col-reverse gap-3 sm:flex-row sm:items-start'>
      {description && (
        <BlockMarkdown className='min-w-0 flex-1 text-sm leading-relaxed text-foreground [&_p]:my-0'>
          {description}
        </BlockMarkdown>
      )}
      <PrerequisitesBox items={prerequisites} className='sm:w-44 sm:shrink-0' />
    </div>
  );
}

function Frame({
  eyebrow,
  title,
  onClose,
  onBack,
  headingRef,
  footer,
  children,
}: {
  eyebrow: string;
  title: string;
  onClose: () => void;
  onBack?: () => void;
  headingRef: React.RefObject<HTMLHeadingElement>;
  footer: ReactNode;
  children: ReactNode;
}) {
  return (
    <>
      <header className='flex items-start gap-2 border-b border-border px-4 py-3'>
        {onBack && (
          <button
            type='button'
            onClick={onBack}
            aria-label='Back'
            className='tap-target -ml-1 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
          >
            <ArrowLeft className='h-4 w-4' aria-hidden />
          </button>
        )}
        <div className='min-w-0 flex-1'>
          <p className='truncate font-mono text-xs uppercase tracking-wide text-muted-foreground'>
            <InlineMarkdown>{eyebrow}</InlineMarkdown>
          </p>
          <h3
            ref={headingRef}
            tabIndex={-1}
            className='text-base font-semibold leading-snug text-foreground outline-none focus-visible:outline-none'
          >
            <InlineMarkdown>{title}</InlineMarkdown>
          </h3>
        </div>
        <button
          type='button'
          onClick={onClose}
          aria-label='Close concept'
          className='tap-target rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
        >
          <X className='h-4 w-4' aria-hidden />
        </button>
      </header>
      {/* Focusable: a concept with a picture scrolls, and a keyboard user
          must be able to scroll it (axe scrollable-region-focusable). */}
      <div
        tabIndex={0}
        role='region'
        aria-label={`About ${title}`}
        className='flex-1 space-y-5 overflow-y-auto px-4 py-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring'
      >
        {children}
      </div>
      <footer className='border-t border-border px-4 py-2.5'>{footer}</footer>
    </>
  );
}

function Progress({ mindspaceId }: { mindspaceId: string }) {
  const { data } = useConceptDetail(mindspaceId);
  if (!data) {
    return <p className='text-xs text-muted-foreground'>In your Mindspace</p>;
  }
  return <MasteryBadge level={deriveMastery(data)} />;
}
